import { PrintTemplateSchema, type PrintTemplate } from "@repo/types";
import {
  ARBRE_FIELDS,
  ARBRE_SECTIONS,
  DEFAULT_ARBRE_TEMPLATE,
  validateTemplate,
  type PrintFieldDef,
} from "@repo/print-pdf/arbre/print/template";
import {
  DEFAULT_EPAYSAGE_TEMPLATE,
  EPAYSAGE_FIELDS,
  EPAYSAGE_SECTIONS,
} from "@repo/print-pdf/epaysage/template";
import { useEffectivePrintTemplate } from "@repo/ui/hooks/api/print-templates";
import { useMemo } from "react";

/**
 * Fiche pilotée par un modèle : activée par défaut. `VITE_PRINT_TEMPLATES=false` (à la construction) est un
 * interrupteur de secours qui rétablit la fiche historique partout, sans appel au serveur.
 */
export const PRINT_TEMPLATES_ENABLED =
  import.meta.env.VITE_PRINT_TEMPLATES !== "false";

export type TemplateVariantKey = "ARBRE" | "EPAYSAGE";

const registryOf = (variant: TemplateVariantKey) =>
  variant === "ARBRE"
    ? {
        sections: ARBRE_SECTIONS,
        fields: ARBRE_FIELDS as PrintFieldDef<any>[],
        defaultTemplate: DEFAULT_ARBRE_TEMPLATE,
      }
    : {
        sections: EPAYSAGE_SECTIONS,
        fields: EPAYSAGE_FIELDS as PrintFieldDef<any>[],
        defaultTemplate: DEFAULT_EPAYSAGE_TEMPLATE,
      };

/** Modèle du serveur s'il est lisible (schéma + registre), sinon `null` : on retombe alors sur la fiche par défaut. */
export function parseServerTemplate(
  config: unknown,
  variant: TemplateVariantKey = "ARBRE",
): PrintTemplate | null {
  const parsed = PrintTemplateSchema.safeParse(config);
  if (!parsed.success || parsed.data.inventoryType !== variant) return null;
  const { sections, fields } = registryOf(variant);
  return validateTemplate(parsed.data, sections, fields).length === 0
    ? parsed.data
    : null;
}

/**
 * Modèle appliqué à une fiche : celui de l'organisation de l'utilisateur, enregistré sur le serveur.
 * Sans modèle (ou si le serveur est injoignable), le modèle par défaut reproduit la fiche historique :
 * l'impression n'est jamais bloquée. `isLoading` laisse les pages attendre le modèle pour ne pas afficher
 * la fiche par défaut puis la remplacer. `isCustom` indique qu'un modèle de l'organisation s'applique.
 */
export function useEffectiveTemplate(variant: TemplateVariantKey): {
  template: PrintTemplate;
  isCustom: boolean;
  isLoading: boolean;
} {
  const query = useEffectivePrintTemplate(variant, PRINT_TEMPLATES_ENABLED);
  const custom = useMemo(
    () => parseServerTemplate(query.data?.config, variant),
    [query.data?.config, variant],
  );
  return {
    template: custom ?? registryOf(variant).defaultTemplate,
    isCustom: custom !== null,
    isLoading: PRINT_TEMPLATES_ENABLED && query.isLoading,
  };
}

/** Modèle de la fiche arbre (la fiche arbre passe toujours par le modèle, par défaut compris). */
export function useEffectiveArbreTemplate() {
  const { template, isLoading } = useEffectiveTemplate("ARBRE");
  return { template, isLoading };
}
